import { ScoringTypeModalProps, ScoringTypeValue } from './types/scoringType';
import DialogModal from '../../components/DialogModal';
import { Box, Button, CircularProgress, TextField } from '@mui/material';
import { StyledButton } from '../Exams/styled';
import MultiValueInput from '../../components/MultiValueInput';
import DropdownSelect from '../../components/DropdownSelect';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import scoringTypes from '../../apis/scoringTypes';

const SCORING_TYPE_OPTIONS = [
  { value: ScoringTypeValue.NUMERIC, label: 'Numeric' },
  { value: ScoringTypeValue.DROPDOWN, label: 'Dropdown (Valid Scores)' },
  { value: ScoringTypeValue.TEXT, label: 'Text (Free Text)' }
];

const DEFAULT_TEXT_MAX_LENGTH = 500;

const AddScoringTypeModal: React.FC<ScoringTypeModalProps> = ({
  fetchData,
  open,
  onClose,
  onApiSuccessAndFailure
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [name, setName] = useState<string>('');
  const [type, setType] = useState<ScoringTypeValue>(
    ScoringTypeValue.NUMERIC
  );
  const [validScores, setValidScores] = useState<string[]>([]);
  const [maxLength, setMaxLength] = useState<number>(DEFAULT_TEXT_MAX_LENGTH);

  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  const handleAddScore = useCallback((score: string) => {
    setValidScores((prev) => [...prev, score]);
  }, []);

  const handleRemoveScore = useCallback((indexToRemove: number) => {
    setValidScores((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  }, []);

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const payload = {
      name,
      type,
      validScores:
        type === ScoringTypeValue.DROPDOWN && validScores.length > 0
          ? validScores
          : null,
      ...(type === ScoringTypeValue.TEXT ? { maxLength } : {})
    };

    try {
      const response = await scoringTypes.upsertScoringType(
        loggedInUser.school,
        payload
      );
      if (response) {
        fetchData();
        const message = `Scoring type added successfully`;
        onApiSuccessAndFailure(true, message);
      }
    } catch (error: any) {
      onApiSuccessAndFailure(false, error.errors[0].message);
    } finally {
      setIsSubmitting(false);
      setName('');
      setType(ScoringTypeValue.NUMERIC);
      setValidScores([]);
      setMaxLength(DEFAULT_TEXT_MAX_LENGTH);
    }
  };

  return (
    <>
      <DialogModal
        open={open}
        onClose={onClose}
        title='Add Scoring Type'
        actions={
          <>
            <Button onClick={onClose} color='primary' variant='contained'>
              Cancel
            </Button>
            <StyledButton
              onClick={handleSubmit}
              disabled={isSubmitting || !name.trim()}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Add'
              )}
            </StyledButton>
          </>
        }
      >
        <Box>
          <TextField
            fullWidth
            id='name'
            name='name'
            label='Scoring Type Name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />

          <Box marginTop={2}>
            <DropdownSelect
              label='Data Type'
              placeholder='Select Data Type'
              name='type'
              value={type}
              options={SCORING_TYPE_OPTIONS}
              onChange={(e) =>
                setType(e.target.value as unknown as ScoringTypeValue)
              }
              fullWidth
            />
          </Box>

          {type === ScoringTypeValue.DROPDOWN && (
            <Box marginTop={2}>
              <MultiValueInput
                values={validScores || []}
                onAddValue={handleAddScore}
                onRemoveValue={handleRemoveScore}
                placeholder='Add valid score and press enter'
                label='Valid Scores'
                validationType='text'
              />
            </Box>
          )}

          {type === ScoringTypeValue.TEXT && (
            <Box marginTop={2}>
              <TextField
                fullWidth
                id='maxLength'
                name='maxLength'
                label='Max Length'
                type='number'
                value={maxLength}
                onChange={(e) =>
                  setMaxLength(parseInt(e.target.value, 10) || 0)
                }
              />
            </Box>
          )}
        </Box>
      </DialogModal>
    </>
  );
};

export default AddScoringTypeModal;
